import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TestTube2, Cog, Gauge, Smartphone, Shield, Layers, Monitor, Users } from 'lucide-react';

const services = [
  {
    icon: TestTube2,
    title: 'Manual Testing',
    description: 'Comprehensive functional and exploratory testing performed by experienced QA professionals.',
    features: ['Functional Testing', 'Regression Testing', 'Integration Testing', 'UAT']
  },
  {
    icon: Cog,
    title: 'Automation Testing',
    description: 'Accelerate testing and improve release quality through scalable automation frameworks.',
    features: ['Selenium', 'Cypress', 'Playwright', 'Appium']
  },
  {
    icon: Gauge,
    title: 'Performance Testing',
    description: 'Evaluate your application\'s speed, scalability, and stability under varying workloads.',
    features: ['Load Testing', 'Stress Testing', 'Endurance Testing', 'JMeter']
  },
  {
    icon: Smartphone,
    title: 'Mobile Testing',
    description: 'Deliver flawless experiences across Android and iOS devices.',
    features: ['Functional Testing', 'Compatibility', 'Usability', 'Performance']
  },
  {
    icon: Shield,
    title: 'Security Testing',
    description: 'Identify vulnerabilities before attackers do with comprehensive security assessments.',
    features: ['Vulnerability Assessment', 'OWASP', 'Authentication', 'Authorization']
  },
  {
    icon: Layers,
    title: 'API Testing',
    description: 'Ensure reliable communication between systems through comprehensive API validation.',
    features: ['REST APIs', 'GraphQL', 'Postman', 'Rest Assured']
  },
  {
    icon: Monitor,
    title: 'Compatibility Testing',
    description: 'Ensure seamless operation across different browsers, devices, and operating systems.',
    features: ['Cross-Browser', 'Multi-Device', 'OS Testing', 'Responsive']
  },
  {
    icon: Users,
    title: 'Accessibility Testing',
    description: 'Build applications that are inclusive and compliant with accessibility standards.',
    features: ['WCAG', 'ADA Compliance', 'Screen Readers', 'Keyboard Navigation']
  },
];

export const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="services" ref={ref} className="py-32 bg-gradient-to-b from-[#0f0f10] via-[#1a1a1a] to-[#0f0f10] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#8B1538] rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
        >
          02
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-[#D4AF37]">QA Testing</span> Services
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Comprehensive testing solutions tailored to your unique needs
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 rounded-sm border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-500 overflow-hidden"
              >
                {/* Hover Gradient Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#8B1538]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-[#1a1a1a]" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-xs text-gray-500 flex items-center gap-2">
                        <span className="w-1 h-1 bg-[#D4AF37] rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};